import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { ChevronRight, Search, Settings, SquarePen } from "lucide-react";
import type { ShellProps, SidebarAreaNode, SidebarModel, SidebarProjectNode, SidebarSpaceNode } from "../types";
import { ProjectGlyph, DeskMark } from "@/components/icons";
import { Tooltip } from "@/components/ui/misc";
import { formatInstant, localZone } from "@/lib/dates";
import { useStored } from "@/lib/storage";
import { cn, modKey } from "@/lib/utils";
import { iconButton, SpaceBadge } from "./chrome";
import { ShellContext, type Crumb } from "./context";
import { ContainerGlyph } from "@/components/container-icon";

const row =
  "group/item relative flex h-7 items-center gap-2 rounded-md pl-2 pr-1 text-[13px] text-sidebar-foreground outline-none transition-colors duration-75 hover:bg-sidebar-accent focus-within:ring-0 has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-ring/50";
const activeRow = "bg-sidebar-accent font-medium text-foreground";

function Hover({ children }: { children: ReactNode }) {
  return (
    <span className="ml-auto hidden shrink-0 items-center group-hover/item:flex group-focus-within/item:flex has-[[data-state=open]]:flex [&_button]:size-6">
      {children}
    </span>
  );
}

function Caret({ expanded, onToggle, name, alwaysVisible }: { expanded: boolean; onToggle: () => void; name: string; alwaysVisible?: boolean }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={expanded ? `Collapse ${name}` : `Expand ${name}`}
      aria-expanded={expanded}
      className={cn(
        "inline-flex size-5 shrink-0 items-center justify-center rounded text-subtle outline-none hover:text-foreground focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring/50",
        !alwaysVisible && "opacity-0 group-hover/item:opacity-100",
      )}
    >
      <ChevronRight className={cn("size-3 transition-transform duration-100", expanded && "rotate-90")} />
    </button>
  );
}

function ProjectRow({ node, slots }: { node: SidebarProjectNode; slots: ShellProps["slots"] }) {
  return (
    <div className={cn(row, "ml-[22px]", node.active && activeRow)}>
      <a href={node.href} className="flex min-w-0 flex-1 items-center gap-2 outline-none" aria-current={node.active ? "page" : undefined}>
        <ProjectGlyph state={node.project.state} progress={node.progress} className="size-3.5 text-foreground/55" />
        <span className="truncate">{node.project.name}</span>
      </a>
      <Hover>{slots.containerActions(node)}</Hover>
    </div>
  );
}

function SpaceRow({ node, slots }: { node: SidebarSpaceNode; slots: ShellProps["slots"] }) {
  const hasProjects = node.projects.length > 0;
  return (
    <div>
      <div className={cn(row, node.active && activeRow)}>
        <a href={node.href} className="flex min-w-0 flex-1 items-center gap-2 outline-none" aria-current={node.active ? "page" : undefined}>
          <ContainerGlyph of={node.space} className="size-4" fallback={<SpaceBadge name={node.space.name} />} />
          <span className="truncate">{node.space.name}</span>
        </a>
        <Hover>{slots.containerActions(node)}</Hover>
        {hasProjects && <Caret expanded={node.expanded} onToggle={node.onToggle} name={node.space.name} />}
      </div>
      {hasProjects && node.expanded && (
        <div className="mt-px flex flex-col gap-px">
          {node.projects.map((p) => (
            <ProjectRow key={p.project.id} node={p} slots={slots} />
          ))}
        </div>
      )}
    </div>
  );
}

function AreaGroup({ node, slots }: { node: SidebarAreaNode; slots: ShellProps["slots"] }) {
  return (
    <div className="mt-4">
      <div className={cn("group/item flex h-6 items-center gap-0.5 rounded-md pl-2 pr-1", node.active && "bg-sidebar-accent")}>
        <a
          href={node.href}
          aria-current={node.active ? "page" : undefined}
          className="min-w-0 truncate rounded text-[12px] font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          {node.area.name}
        </a>
        <Caret expanded={node.expanded} onToggle={node.onToggle} name={node.area.name} />
        <Hover>{slots.areaActions(node)}</Hover>
      </div>
      {node.expanded && (
        <div className="mt-0.5 flex flex-col gap-px">
          {node.spaces.map((s) => (
            <SpaceRow key={s.space.id} node={s} slots={slots} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Area › Space ancestors of the active page, from the sidebar model. */
function useCrumbs(sidebar: SidebarModel): Crumb[] {
  return useMemo(() => {
    for (const a of sidebar.areas) {
      const areaCrumb = { label: a.area.name, href: a.href };
      for (const s of a.spaces) {
        if (s.active) return [areaCrumb];
        for (const p of s.projects) if (p.active) return [areaCrumb, { label: s.space.name, href: s.href }];
      }
    }
    for (const n of sidebar.settled) {
      if (!n.active) continue;
      const areaId = n.kind === "space" ? n.space.areaId : n.project.areaId;
      const area = sidebar.areas.find((a) => a.area.id === areaId);
      const out: Crumb[] = area ? [{ label: area.area.name, href: area.href }] : [];
      if (n.kind === "project") {
        const space = area?.spaces.find((s) => s.space.id === n.project.spaceId);
        if (space) out.push({ label: space.space.name, href: space.href });
      }
      return out;
    }
    return [];
  }, [sidebar]);
}

const PANEL_MIN = 380;
const PANEL_DEFAULT = 600;

/** Right-hand detail panel with a draggable, keyboard-resizable left edge. */
function DetailPanel({ children }: { children: ReactNode }) {
  const [width, setWidth] = useStored("console.panelWidth", PANEL_DEFAULT);
  const drag = useRef<{ x: number; w: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const max = () => Math.max(PANEL_MIN, Math.round(window.innerWidth * 0.62));
  const clamp = (w: number) => Math.min(max(), Math.max(PANEL_MIN, Math.round(w)));
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, w: width };
    setDragging(true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    setWidth(clamp(drag.current.w + (drag.current.x - e.clientX)));
  };
  const end = () => {
    drag.current = null;
    setDragging(false);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 64 : 16;
    if (e.key === "ArrowLeft") (e.preventDefault(), e.stopPropagation(), setWidth(clamp(width + step)));
    if (e.key === "ArrowRight") (e.preventDefault(), e.stopPropagation(), setWidth(clamp(width - step)));
  };
  return (
    <aside aria-label="Task detail" className="relative flex shrink-0 flex-col border-l border-border bg-background" style={{ width: clamp(width) }}>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize detail panel"
        aria-valuemin={PANEL_MIN}
        aria-valuemax={max()}
        aria-valuenow={clamp(width)}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={end}
        onPointerCancel={end}
        onDoubleClick={() => setWidth(PANEL_DEFAULT)}
        onKeyDown={onKeyDown}
        className={cn(
          "absolute inset-y-0 -left-[3px] z-30 w-[6px] cursor-col-resize outline-none",
          "after:absolute after:inset-y-0 after:left-[2px] after:w-[2px] after:transition-colors after:duration-100 hover:after:bg-foreground/25 focus-visible:after:bg-foreground/50",
          dragging && "after:bg-foreground/40",
        )}
      />
      <div className="min-h-0 flex-1">{children}</div>
    </aside>
  );
}

export function ConsoleShell({ sidebar, slots, sidebarCollapsed, onToggleSidebar, detailPanel, children }: ShellProps) {
  const tz = localZone();
  const crumbs = useCrumbs(sidebar);
  const shellState = useMemo(
    () => ({ crumbs, toolbar: slots.toolbar, sidebarCollapsed, onToggleSidebar }),
    [crumbs, slots.toolbar, sidebarCollapsed, onToggleSidebar],
  );
  return (
    <ShellContext.Provider value={shellState}>
      <div className="flex h-full w-full overflow-hidden bg-sidebar">
        {!sidebarCollapsed && (
          <aside aria-label="Sidebar" className="flex w-[236px] shrink-0 flex-col text-sidebar-foreground">
            <div className="desk-drag desk-titlebar flex h-12 shrink-0 items-center gap-1 pl-4 pr-2">
              <span className="flex items-center gap-1.5 font-display text-[16px] font-bold tracking-[-0.02em] text-foreground">
              <DeskMark className="size-[18px]" />
              Desk
            </span>
              <div className="desk-no-drag ml-auto flex items-center gap-1">
                <Tooltip content="Search" shortcut={`${modKey}K`}>
                  <button type="button" aria-label="Search" onClick={slots.onSearch} className={iconButton}>
                    <Search />
                  </button>
                </Tooltip>
                <Tooltip content="New task" shortcut="C">
                  <button
                    type="button"
                    aria-label="New task"
                    onClick={slots.onQuickAdd}
                    className={cn(iconButton, "border border-border bg-foreground/[0.04] text-foreground shadow-[0_1px_0_rgba(0,0,0,0.04)] [&_svg]:size-[15px]")}
                  >
                    <SquarePen />
                  </button>
                </Tooltip>
              </div>
            </div>

            <nav aria-label="Views" className="flex-1 overflow-y-auto px-2 pb-3 pt-1">
              <ul className="flex flex-col gap-px">
                {sidebar.nav.map((item) => (
                  <li key={item.id}>
                    <a href={item.href} aria-current={item.active ? "page" : undefined} className={cn(row, "pr-2", item.active && activeRow)}>
                      <item.icon
                        className={cn(
                          "size-[15px] shrink-0",
                          item.id === "today" ? "fill-[var(--star)] text-[var(--star)]" : item.active ? "text-foreground" : "text-foreground/60",
                        )}
                        strokeWidth={1.8}
                        aria-hidden
                      />
                      <span className="truncate">{item.label}</span>
                      {item.count !== undefined && item.count > 0 && (
                        <span
                          className={cn(
                            "ml-auto text-[11.5px] font-normal tabular",
                            item.id === "review" ? "rounded-full bg-foreground/10 px-1.5 leading-[18px] font-medium text-foreground/85" : "text-subtle",
                          )}
                        >
                          {item.count}
                        </span>
                      )}
                    </a>
                  </li>
                ))}
              </ul>

              {sidebar.areas.map((a) => (
                <AreaGroup key={a.area.id} node={a} slots={slots} />
              ))}

              <div className="mt-3 [&_button]:h-7 [&_button]:text-[12.5px]">{slots.newArea}</div>

              {sidebar.settled.length > 0 && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={sidebar.onToggleSettled}
                    aria-expanded={sidebar.settledExpanded}
                    className="group/item flex h-6 w-full items-center gap-0.5 rounded-md px-2 text-[12px] font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50"
                  >
                    Settled
                    <ChevronRight className={cn("ml-0.5 size-3 text-subtle transition-transform duration-100", sidebar.settledExpanded && "rotate-90")} />
                    <span className="ml-auto font-normal text-subtle tabular">{sidebar.settled.length}</span>
                  </button>
                  {sidebar.settledExpanded && (
                    <div className="mt-0.5 flex flex-col gap-px">
                      {sidebar.settled.map((n) => (
                        <div key={n.kind === "space" ? n.space.id : n.project.id} className={cn(row, n.active && activeRow)}>
                          <a href={n.href} className="flex min-w-0 flex-1 items-center gap-2 text-foreground/60 outline-none" aria-current={n.active ? "page" : undefined}>
                            {n.kind === "space" ? (
                              <SpaceBadge name={n.space.name} className="opacity-70" />
                            ) : (
                              <ProjectGlyph state={n.project.state} progress={n.progress} className="size-3.5" />
                            )}
                            <span className="truncate">{n.kind === "space" ? n.space.name : n.project.name}</span>
                            {n.snoozedUntil && (
                              <span className="ml-auto shrink-0 text-[11.5px] text-subtle group-hover/item:hidden">
                                {formatInstant(n.snoozedUntil, tz).replace(/:00 /, " ")}
                              </span>
                            )}
                          </a>
                          <Hover>{slots.containerActions(n)}</Hover>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </nav>

            <div className="flex h-10 shrink-0 items-center gap-1 px-2">
              <div className="min-w-0 flex-1">{slots.syncStatus}</div>
              <Tooltip content="Settings" shortcut={`${modKey},`}>
                <a
                  href={slots.settingsHref}
                  aria-label="Settings"
                  aria-current={slots.settingsActive ? "page" : undefined}
                  className={cn(iconButton, slots.settingsActive && "bg-sidebar-accent text-foreground")}
                >
                  <Settings />
                </a>
              </Tooltip>
            </div>
          </aside>
        )}

        <div className={cn("flex min-w-0 flex-1 py-2 pr-2", sidebarCollapsed && "pl-2")}>
          <div className="flex min-w-0 flex-1 overflow-hidden rounded-[10px] border border-border bg-background shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
            <main className="@container/main relative flex min-w-0 flex-1 flex-col">
              <div id="desk-main-scroll" className="flex-1 overflow-y-auto">
                <div className={cn("min-h-full px-4 pb-16", slots.settingsActive && "[&>div]:px-4 [&>div]:pt-6")}>{children}</div>
              </div>
            </main>
            {detailPanel && <DetailPanel>{detailPanel}</DetailPanel>}
          </div>
        </div>
      </div>
    </ShellContext.Provider>
  );
}
